import { useState } from 'react';
import type { ApiEndpoints, EngineWorkscopeEventWithDocument } from '@avisoma/shared';
import { FaFile } from 'react-icons/fa6';
import { Card } from '../../components/Card';
import { DescriptionList, DescriptionTerm, DescriptionDetails } from '../../catalyst/description-list';
import { ExtractionTableCard } from '../../components/reconstructedDocument/extractionTables/ExtractionTableCard';
import { RowInspectorDialog } from '../../components/reconstructedDocument/extractionTables/RowInspectorDialog';
import {
  formatModulesWorked,
  formatWorkOrders,
  formatWorkscopeClock,
  formatWorkscopeLevel,
} from '../../components/reconstructedDocument/extractionTables/workscopePresentation';
import { PeekLink } from '../../components/PeekLink';
import { ID } from '../../components/ID';
import { PageChips } from './PageChips';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTdClass,
  extractionThClass,
  extractionTheadClass,
} from '../../components/reconstructedDocument/extractionTables/tableClasses';

interface WorkscopeEventsProps {
  response: ApiEndpoints<string>['/api/projects/:id']['GET']['response'];
}

/**
 * Project-level shop-visit workscope events (DR-3126). Every Engine-Workscope document
 * in the project contributes to one flat array — the Bill-of-Sale union posture — so
 * this mirrors `BillOfSaleEvents`: one row per shop visit, each linking back to its
 * evidence pages and its representative document.
 */
export function WorkscopeEvents({ response }: WorkscopeEventsProps) {
  const rawEvents = response.item.data?.workscopeEvents?.workscopeEvents;
  const [inspected, setInspected] = useState<EngineWorkscopeEventWithDocument | null>(null);

  if (!rawEvents || !rawEvents.length) {
    return null;
  }

  // An event can arrive without pageIds (runtime violates the string[] type, e.g. while a
  // project is mid-reprocessing). Default to [] so .join / PageChips don't crash on undefined.
  const events = rawEvents.map((event) => ({ ...event, pageIds: event.pageIds ?? [] }));
  const csvData = events.map((event) => ({
    ...event,
    pageIds: event.pageIds.join(' '),
    modulesWorked: event.modulesWorked?.join(' | ') ?? '',
    workOrders: event.workOrders?.join(' | ') ?? '',
  }));

  return (
    <div className="space-y-6">
      <Card
        header={
          <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Engine Workscope</h2>
        }
      >
        <DescriptionList>
          <DescriptionTerm>Shop Visit Count</DescriptionTerm>
          <DescriptionDetails>{events.length}</DescriptionDetails>
        </DescriptionList>
      </Card>

      <ExtractionTableCard
        label="Engine Workscope"
        rows={csvData}
        csvTitle="Engine Workscope Events"
        bodyHeight="project"
      >
        <table className={extractionTableClass}>
          <thead className={extractionTheadClass}>
            <tr>
              <th className={extractionThClass}>Shop Visit</th>
              <th className={extractionThClass}>Work Orders</th>
              <th className={extractionThClass}>Level</th>
              <th className={extractionThClass}>Modules Worked</th>
              <th className={extractionThClass}>Date</th>
              <th className={extractionThClass}>TSN</th>
              <th className={extractionThClass}>CSN</th>
              <th className={extractionThClass}>TSLSV</th>
              <th className={extractionThClass}>CSLSV</th>
              <th className={extractionThClass}>Facility</th>
              <th className={extractionThClass}>Source Pages</th>
              <th className={extractionThClass}>Document</th>
            </tr>
          </thead>
          <tbody className={extractionTbodyClass}>
            {events.map((event, index) => {
              const modules = formatModulesWorked(event.modulesWorked);
              const workOrders = formatWorkOrders(event.workOrders);
              return (
                <tr
                  key={`${event.reconstructedDocumentId}-${event.shopVisitRef ?? 'visit'}-${index}`}
                  onClick={() => setInspected(event)}
                  className={extractionRowClass}
                >
                  <td className={`${extractionTdClass} font-mono`}>{event.shopVisitRef ?? '—'}</td>
                  <td className={`${extractionTdClass} font-mono`} title={workOrders}>
                    {workOrders ?? '—'}
                  </td>
                  <td className={extractionTdClass}>
                    {formatWorkscopeLevel(event.workscopeLevel) ?? '—'}
                  </td>
                  <td className={extractionTdClass} title={modules}>
                    {modules ?? '—'}
                  </td>
                  <td className={extractionTdClass}>{event.date ?? '—'}</td>
                  <td className={extractionTdClass}>{formatWorkscopeClock(event.tsn) ?? '—'}</td>
                  <td className={extractionTdClass}>{formatWorkscopeClock(event.csn) ?? '—'}</td>
                  <td className={extractionTdClass}>{formatWorkscopeClock(event.tslsv) ?? '—'}</td>
                  <td className={extractionTdClass}>{formatWorkscopeClock(event.cslsv) ?? '—'}</td>
                  <td className={extractionTdClass} title={event.facility}>
                    {event.facility ?? '—'}
                  </td>
                  <td className={extractionTdClass}>
                    <div onClick={(e) => e.stopPropagation()}>
                      <PageChips pageIds={event.pageIds} />
                    </div>
                  </td>
                  <td className={extractionTdClass}>
                    <div className="flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
                      {event.reconstructedDocumentId && (
                        <PeekLink
                          type="reconstructed"
                          id={event.reconstructedDocumentId}
                          className="inline-flex items-center gap-1"
                        >
                          <FaFile className="size-3" />
                          <ID id={event.reconstructedDocumentId} />
                        </PeekLink>
                      )}
                      {event.documentId && (
                        <PeekLink
                          type="document"
                          id={event.documentId}
                          className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-zinc-400"
                        >
                          <FaFile className="size-3" />
                          <ID id={event.documentId} />
                        </PeekLink>
                      )}
                      {!event.reconstructedDocumentId && !event.documentId && '—'}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ExtractionTableCard>

      <RowInspectorDialog
        open={inspected != null}
        onClose={() => setInspected(null)}
        title="Engine Workscope row"
        data={inspected}
      />
    </div>
  );
}
